import { generateKeyBetween } from 'fractional-indexing';
import { WORLD_LIMIT, type ObjectData } from './schema';

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Per-object geometry repair: inside the world, never smaller than 1 px (so always visible and selectable). */
export function repairGeometry<T extends ObjectData>(o: T): T {
  const x = clamp(o.x, -WORLD_LIMIT, WORLD_LIMIT);
  const y = clamp(o.y, -WORLD_LIMIT, WORLD_LIMIT);
  const w = Math.max(1, o.w);
  const h = Math.max(1, o.h);
  return x === o.x && y === o.y && w === o.w && h === o.h ? o : { ...o, x, y, w, h };
}

/**
 * Deterministic repair applied at load time: orphaned or cyclic parents, out-of-bounds geometry, duplicate z.
 * Pure: returns a new map and the list of fixes (empty when the document was already valid).
 */
export function normalizeObjects(input: ReadonlyMap<string, ObjectData>): { objects: Map<string, ObjectData>; fixes: string[] } {
  const fixes: string[] = [];
  const out = new Map<string, ObjectData>();

  for (const [id, o] of input) {
    let next = o;
    if (o.parentId !== null && !input.has(o.parentId)) {
      next = { ...next, parentId: null };
      fixes.push(`orphan:${id}`);
    }
    const repaired = repairGeometry(next);
    if (repaired !== next) {
      next = repaired;
      fixes.push(`geometry:${id}`);
    }
    out.set(id, next);
  }

  // Parent cycles (an import or a forged peer; commands never create one): broken at the loop's smallest id,
  // so every peer repairing the same state makes the same choice.
  const parentOf = (id: string) => out.get(id)?.parentId ?? null;
  for (const start of [...out.keys()].sort()) {
    const path: string[] = [];
    let cur: string | null = start;
    while (cur !== null && !path.includes(cur)) {
      path.push(cur);
      cur = parentOf(cur);
    }
    if (cur === null) continue;
    const cut = path.slice(path.indexOf(cur)).reduce((a, b) => (b < a ? b : a));
    out.set(cut, { ...out.get(cut)!, parentId: null });
    fixes.push(`cycle:${cut}`);
  }

  // z ties: order by (z, id), then give every duplicate a fresh key right above its predecessor.
  const sorted = [...out.values()].sort((a, b) => (a.z < b.z ? -1 : a.z > b.z ? 1 : a.id < b.id ? -1 : 1));
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]!;
    const cur = sorted[i]!;
    if (cur.z > prev.z) continue; // `prev` may already hold a fresh key: a third tied object sorts below it
    const upper = sorted.slice(i + 1).find((o) => o.z > prev.z)?.z ?? null;
    const z = generateKeyBetween(prev.z, upper);
    sorted[i] = { ...cur, z };
    out.set(cur.id, sorted[i]!);
    fixes.push(`z:${cur.id}`);
  }
  return { objects: out, fixes };
}
